import * as React from "react";
import { useLocation } from "wouter";
import { ArrowLeft, Palette, Image, Images, Trash2 } from "lucide-react";

const BACKGROUND_STORAGE_KEY = "rememly-capture-background";

const OPTIONS = [
  {
    icon: Palette,
    title: "Themes",
    description: "Choose a complete look for Rememly.",
  },
  {
    icon: Image,
    title: "My Backgrounds",
    description: "Use an image from your gallery as your background.",
  },
  {
    icon: Images,
    title: "Backgrounds",
    description: "Choose from Rememly's collection of backgrounds.",
  },
];

export function AppearancePage() {
  const [, setLocation] = useLocation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [hasBg, setHasBg] = React.useState(false);
  React.useEffect(() => {
    setHasBg(!!localStorage.getItem(BACKGROUND_STORAGE_KEY));
  }, []);

  const handleRemoveBg = () => {
    localStorage.removeItem(BACKGROUND_STORAGE_KEY);
    setHasBg(false);
  };

  const handleClick = (title: string) => {
    if (title === "Themes") {
      setLocation("/themes");
    } else if (title === "My Backgrounds") {
      setError(null);
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      try {
        localStorage.setItem(BACKGROUND_STORAGE_KEY, dataUrl);
        setHasBg(true);
        setLocation("/");
      } catch {
        setError(
          "That image is too large to save. Try a smaller photo.",
        );
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="max-w-4xl mx-auto min-h-[calc(100dvh-4rem)] flex flex-col px-4">
      <div className="pt-6 pb-4 shrink-0 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) {
              window.history.back();
            } else {
              setLocation("/");
            }
          }}
          className="h-9 w-9 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold">Appearance</h2>
      </div>

      {error && (
        <p className="text-sm text-destructive mb-3 px-1">{error}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex flex-col gap-3 pb-6">
        {/* "Backgrounds" (built-in collection) is a placeholder — not wired up yet */}
        {OPTIONS.map(({ icon: Icon, title, description }) => (
          <button
            key={title}
            type="button"
            onClick={() => handleClick(title)}
            className="flex items-start gap-3 text-left bg-card border rounded-2xl px-4 py-4 shadow-sm hover:border-primary/40 transition-colors"
          >
            <div
              className="h-10 w-10 shrink-0 flex items-center justify-center"
            >
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold">{title}</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {description}
              </p>
            </div>
          </button>
        ))}
        {hasBg && (
          <button
            type="button"
            onClick={handleRemoveBg}
            className="flex items-start gap-3 text-left bg-card border rounded-2xl px-4 py-4 shadow-sm hover:border-destructive/40 transition-colors"
          >
            <div className="h-10 w-10 shrink-0 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-500" />
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-red-500">
                Remove Background
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Go back to your current theme's default background.
              </p>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
